import React, { useState } from 'react';
import { Tag, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TagObject {
  id: string;
  name: string;
}

interface EnhancedTagsInputProps {
  tags: string[]; // This will store tag IDs
  setTags: (tags: string[]) => void;
  availableTags: TagObject[]; // This will be tag objects with id and name
  disabled?: boolean;
  maxDisplayCount?: number;
}

export const EnhancedTagsInput = ({
  tags,
  setTags,
  availableTags,
  disabled = false,
  maxDisplayCount = 3
}: EnhancedTagsInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const displayTags = tags.slice(0, maxDisplayCount);
  const remainingCount = tags.length - maxDisplayCount;
  
  const availableTagsToShow = availableTags.filter(tag => !tags.includes(tag.id));

  const handleAddTag = (tagId: string) => {
    if (!tags.includes(tagId)) {
      setTags([...tags, tagId]);
    }
  };

  const handleRemoveTag = (tagIdToRemove: string) => {
    setTags(tags.filter(tagId => tagId !== tagIdToRemove));
  };

  const getTagName = (tagId: string) => {
    const tag = availableTags.find(t => t.id === tagId);
    return tag?.name || tagId;
  };

  return (
    <div className="space-y-2">
      {/* Display selected tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {displayTags.map((tagId, index) => (
            <Badge 
              key={index} 
              variant="secondary"
              className="flex items-center gap-1 py-1 pl-2 pr-1"
            >
              <Tag className="h-3 w-3 opacity-70" />
              {getTagName(tagId)}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tagId)}
                  className="rounded-full hover:bg-muted focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
          
          {remainingCount > 0 && (
            <Badge variant="outline" className="flex items-center gap-1 py-1">
              +{remainingCount} more
            </Badge>
          )}
        </div>
      )}
      
      {/* Tag selector */}
      <Select 
        open={isOpen} 
        onOpenChange={setIsOpen}
        onValueChange={(value) => {
          if (value) {
            handleAddTag(value);
            // Reset the select value so the same tag can be selected again if needed
            setTimeout(() => {
              const selectElement = document.querySelector('[data-radix-select-trigger]') as HTMLButtonElement;
              if (selectElement) {
                selectElement.click();
              }
            }, 100);
          }
        }}
        disabled={disabled || availableTagsToShow.length === 0}
      >
        <SelectTrigger className="w-full focus:ring-primary/30 focus:border-primary">
          <SelectValue placeholder={availableTagsToShow.length === 0 ? "No tags available" : "Add tags..."} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableTagsToShow.map((tag) => (
              <SelectItem key={tag.id} value={tag.id}>
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span>{tag.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};
